import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { collection, onSnapshot, query, where, orderBy, updateDoc, doc, in as firestoreIn } from "firebase/firestore";
import { db } from "../config/firebase";
import { useAuth } from "./AuthContext";

interface Message {
  id: string;
  userId: string;
  sender: string;
  content: string;
  timestamp: Date;
  read: boolean;
  notified: boolean;
}

interface Notification {
  id: string;
  userId: string;
  title: string;
  content: string;
  type: 'success' | 'warning' | 'error' | 'info';
  timestamp: Date;
  read: boolean;
  link?: string;
}

interface NotificationsContextType {
  messages: Message[];
  notifications: Notification[];
  unreadMessages: number;
  unreadNotifications: number;
  markMessageAsRead: (messageId: string) => Promise<void>;
  markNotificationAsRead: (notificationId: string) => Promise<void>;
  markAllMessagesAsRead: () => Promise<void>;
  markAllNotificationsAsRead: () => Promise<void>;
}

const NotificationsContext = createContext<NotificationsContextType>({
  messages: [],
  notifications: [],
  unreadMessages: 0,
  unreadNotifications: 0,
  markMessageAsRead: async () => {},
  markNotificationAsRead: async () => {},
  markAllMessagesAsRead: async () => {},
  markAllNotificationsAsRead: async () => {},
});

export function NotificationsProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [notifications, setNotifications] = useState<Notification[]>([]);

  useEffect(() => {
    if (!user) {
      setMessages([]);
      setNotifications([]);
      return;
    }

    // Listener para mensagens
    const messagesQuery = query(
      collection(db, 'messages'),
      where('recipientId', '==', user.uid),
      orderBy('timestamp', 'desc')
    );

    const unsubscribeMessages = onSnapshot(messagesQuery, (snapshot) => {
      const newMessages = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate() || new Date(),
      })) as Message[];
      setMessages(newMessages);
    });

    // Listener para notificações - agora busca por userId e companyId
    const notificationsQuery = query(
      collection(db, 'notifications'),
      where('userId', 'in', [user.uid, user.companyId]),
      orderBy('timestamp', 'desc')
    );

    const unsubscribeNotifications = onSnapshot(notificationsQuery, (snapshot) => {
      const newNotifications = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate() || new Date(),
      })) as Notification[];
      setNotifications(newNotifications);
    });

    return () => {
      unsubscribeMessages();
      unsubscribeNotifications();
    };
  }, [user]);

  const unreadMessages = useMemo(() => 
    messages.filter((msg) => !msg.read).length
  , [messages]);

  const unreadNotifications = useMemo(() => 
    notifications.filter((notif) => !notif.read).length
  , [notifications]);

  const markMessageAsRead = async (messageId: string) => {
    if (!user) return;
    const messageRef = doc(db, 'messages', messageId);
    await updateDoc(messageRef, { read: true });
  };

  const markNotificationAsRead = async (notificationId: string) => {
    if (!user) return;
    const notificationRef = doc(db, 'notifications', notificationId);
    await updateDoc(notificationRef, { read: true });
  };

  const markAllMessagesAsRead = async () => {
    if (!user) return;
    const unreadMessages = messages.filter((msg) => !msg.read);
    const updatePromises = unreadMessages.map((msg) =>
      updateDoc(doc(db, 'messages', msg.id), { read: true })
    );
    await Promise.all(updatePromises);
  };

  const markAllNotificationsAsRead = async () => {
    if (!user) return;
    const unreadNotifications = notifications.filter((notif) => !notif.read);
    const updatePromises = unreadNotifications.map((notif) =>
      updateDoc(doc(db, 'notifications', notif.id), { read: true })
    );
    await Promise.all(updatePromises);
  };

  return (
    <NotificationsContext.Provider
      value={{
        messages,
        notifications,
        unreadMessages,
        unreadNotifications,
        markMessageAsRead,
        markNotificationAsRead,
        markAllMessagesAsRead,
        markAllNotificationsAsRead,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
}

export const useNotifications = () => useContext(NotificationsContext);
